import React from 'react';
import { ArrowLeft, Shield, Database, Share2, Clock, UserCheck, Lock, Globe, Users, Bell, Mail } from 'lucide-react';
import './FooterPages.css';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="footer-page">
      <button className="back-button" onClick={onBack}>
        <ArrowLeft size={20} />
        Back
      </button>
      <div className="footer-page-content legal-page">
        <div className="legal-header">
          <Shield size={40} className="legal-icon" />
          <h1>Privacy Policy</h1>
          <div className="legal-dates">
            <span>Last Updated: January 13, 2026</span>
            <span>Effective Date: January 13, 2026</span>
          </div>
        </div>

        <section className="legal-section">
          <h2>Introduction</h2>
          <p>
            Welcome to Research Methodology Wizard ("we," "our," or "the App"). This Privacy Policy explains how we collect, use, store, protect, and share your personal information when you use our web application and services. We are committed to protecting your privacy and ensuring transparency in how your data is handled.
          </p>
          <p>
            By accessing or using Research Methodology Wizard, you agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Database size={24} />
            <h2>What Information We Collect</h2>
          </div>

          <div className="subsection">
            <h3>1. Information You Provide to Us</h3>
            
            <div className="info-card">
              <h4>Account Information</h4>
              <ul>
                <li>Email address</li>
                <li>Username and password</li>
                <li>Name (optional)</li>
                <li>Organization/institution name (optional)</li>
                <li>Billing information (credit/debit card details, billing address) for subscription payments</li>
              </ul>
            </div>

            <div className="info-card">
              <h4>Research Project Data</h4>
              <ul>
                <li>Research questions and objectives you input into Wizard A</li>
                <li>Methodological choices you make in Wizard B</li>
                <li>Survey questionnaires, interview guides, and focus group plans you create</li>
                <li>Data collection instruments you design</li>
                <li>Framework outputs you generate</li>
                <li>Sample size calculations and parameters</li>
                <li>Any custom notes or annotations you add</li>
              </ul>
            </div>

            <div className="info-card">
              <h4>Communication Data</h4>
              <ul>
                <li>Support requests and correspondence</li>
                <li>Feedback and survey responses</li>
                <li>Messages sent through our contact forms</li>
              </ul>
            </div>
          </div>

          <div className="subsection">
            <h3>2. Information Collected Automatically</h3>
            
            <div className="info-card">
              <h4>Usage Data</h4>
              <ul>
                <li>Pages visited and features used</li>
                <li>Time spent on different sections</li>
                <li>Clicks, navigation patterns, and interaction data</li>
                <li>Session duration and frequency of visits</li>
                <li>Device information (browser type, operating system, device type)</li>
                <li>IP address and approximate geographic location</li>
                <li>Referral sources (how you found our app)</li>
              </ul>
            </div>

            <div className="info-card">
              <h4>Technical Data</h4>
              <ul>
                <li>Browser and device identifiers</li>
                <li>Log files and error reports</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>

          <div className="subsection">
            <h3>3. Information from Third Parties</h3>
            
            <div className="info-card">
              <h4>Payment Processors</h4>
              <ul>
                <li>Transaction confirmation data from Stripe or other payment gateways</li>
                <li>Payment status and subscription tier information</li>
              </ul>
            </div>

            <div className="info-card">
              <h4>Authentication Services</h4>
              <p>If you sign up using Google or other OAuth providers, we receive basic profile information (name, email) as permitted by those services</p>
            </div>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <UserCheck size={24} />
            <h2>How We Use Your Information</h2>
          </div>

          <div className="use-grid">
            <div className="use-card">
              <h4>To Provide and Improve Our Services</h4>
              <ul>
                <li>Create and manage your account</li>
                <li>Process your subscription payments</li>
                <li>Enable you to use Wizards A to Wizard E, and all data collection and ethical approval tools</li>
                <li>Generate frameworks, reports, and exports</li>
                <li>Save your progress and preferences</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Fix bugs and technical issues</li>
              </ul>
            </div>

            <div className="use-card">
              <h4>To Enhance User Experience</h4>
              <ul>
                <li>Personalize your experience based on your methodological choices</li>
                <li>Remember your settings and preferences</li>
                <li>Provide contextual tips and guidance</li>
                <li>Auto-save your work to prevent data loss</li>
              </ul>
            </div>

            <div className="use-card">
              <h4>To Analyze and Improve the App</h4>
              <ul>
                <li>Understand how users interact with different features</li>
                <li>Identify which methodologies are most commonly selected</li>
                <li>Improve our algorithms and recommendations</li>
                <li>Develop new features based on user needs</li>
                <li>Test new functionality and A/B experiments</li>
              </ul>
            </div>

            <div className="use-card">
              <h4>To Communicate with You</h4>
              <ul>
                <li>Send subscription confirmations and payment receipts</li>
                <li>Notify you of important updates or changes to our Terms</li>
                <li>Respond to support requests</li>
                <li>Send educational content or tips (with your consent)</li>
                <li>Request feedback on your experience</li>
              </ul>
            </div>

            <div className="use-card">
              <h4>To Ensure Security and Prevent Abuse</h4>
              <ul>
                <li>Detect and prevent fraud or unauthorized access</li>
                <li>Monitor for violations of our Terms and Conditions</li>
                <li>Protect against spam, malware, and security threats</li>
                <li>Enforce our Acceptable Use Policy</li>
              </ul>
            </div>

            <div className="use-card">
              <h4>To Comply with Legal Obligations</h4>
              <ul>
                <li>Respond to legal requests or court orders</li>
                <li>Comply with applicable laws and regulations</li>
                <li>Protect our legal rights and defend against claims</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Share2 size={24} />
            <h2>How We Share Your Information</h2>
          </div>
          
          <div className="highlight-box">
            <p><strong>We respect your privacy and do not sell your personal information to third parties.</strong></p>
          </div>

          <p>We may share your information only in the following circumstances:</p>

          <div className="subsection">
            <h3>1. With Your Consent</h3>
            <ul>
              <li>When you explicitly authorize us to share your information</li>
              <li>When you choose to export or share your research outputs</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>2. With Service Providers</h3>
            <p>We work with trusted third-party service providers who assist us in operating the app:</p>
            <ul>
              <li>Payment processors (e.g., Stripe) to handle subscription payments</li>
              <li>Cloud hosting providers (e.g., AWS, Google Cloud) to store data</li>
              <li>Analytics services (e.g., Google Analytics) to understand usage patterns</li>
              <li>Email service providers to send transactional emails</li>
            </ul>
            <p>These providers are contractually obligated to protect your data and use it only for the purposes we specify.</p>
          </div>

          <div className="subsection">
            <h3>3. For Legal Reasons</h3>
            <ul>
              <li>To comply with legal obligations, court orders, or government requests</li>
              <li>To enforce our Terms and Conditions and protect our rights</li>
              <li>To prevent fraud, security threats, or harm to users or the public</li>
              <li>In connection with legal proceedings or investigations</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>4. Business Transfers</h3>
            <p>If Research Methodology Wizard is acquired by or merged with another company, your information may be transferred to the new owner to ensure continuity of service. We will notify you before your information is transferred and becomes subject to a different privacy policy.</p>
          </div>

          <div className="subsection">
            <h3>5. Aggregated and De-identified Data</h3>
            <p>We may share aggregated, de-identified, or anonymized data that cannot be used to identify you individually. For example:</p>
            <ul>
              <li>"80% of users chose quantitative methodology"</li>
              <li>"Average survey length is 15 questions"</li>
              <li>Usage statistics to improve our services</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Clock size={24} />
            <h2>Data Retention Policy</h2>
          </div>
          
          <p>We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy:</p>

          <div className="retention-table">
            <div className="retention-row">
              <span className="retention-type">Account and profile data</span>
              <span className="retention-period">Retained while your account is active</span>
            </div>
            <div className="retention-row">
              <span className="retention-type">Research project data</span>
              <span className="retention-period">Retained while your account is active and for 90 days after account closure</span>
            </div>
            <div className="retention-row">
              <span className="retention-type">Subscription and billing data</span>
              <span className="retention-period">Retained for 7 years (tax and accounting regulations)</span>
            </div>
            <div className="retention-row">
              <span className="retention-type">Usage and analytics data</span>
              <span className="retention-period">Retained for up to 24 months</span>
            </div>
            <div className="retention-row">
              <span className="retention-type">Support communications</span>
              <span className="retention-period">Retained for 3 years</span>
            </div>
          </div>

          <p>You can request deletion of your data at any time by closing your account or contacting us at <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a>.</p>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <UserCheck size={24} />
            <h2>Your Data Rights and Choices</h2>
          </div>
          
          <p>Depending on your location, you may have the following rights regarding your personal information:</p>

          <div className="rights-grid">
            <div className="right-card">
              <h4>Access and Portability</h4>
              <ul>
                <li>Request a copy of your personal information</li>
                <li>Download your research projects and data in common formats (PDF, DOCX, JSON)</li>
                <li>Receive your data in a structured, machine-readable format</li>
              </ul>
            </div>

            <div className="right-card">
              <h4>Correction and Updates</h4>
              <ul>
                <li>Update your account information at any time through your account settings</li>
                <li>Correct inaccurate or incomplete data</li>
              </ul>
            </div>

            <div className="right-card">
              <h4>Deletion</h4>
              <ul>
                <li>Request deletion of your personal information</li>
                <li>Close your account, which will trigger deletion of most data within 90 days</li>
              </ul>
            </div>

            <div className="right-card">
              <h4>Restriction and Objection</h4>
              <ul>
                <li>Object to certain types of data processing (e.g., marketing communications)</li>
                <li>Restrict how we process your data in certain circumstances</li>
              </ul>
            </div>

            <div className="right-card">
              <h4>Withdrawal of Consent</h4>
              <ul>
                <li>Withdraw your consent for data processing at any time</li>
                <li>Opt out of non-essential cookies through our cookie settings</li>
              </ul>
            </div>

            <div className="right-card">
              <h4>Lodge a Complaint</h4>
              <ul>
                <li>File a complaint with your local data protection authority if you believe we have violated your privacy rights</li>
              </ul>
            </div>
          </div>

          <p>To exercise these rights, contact us at: <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a></p>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Lock size={24} />
            <h2>Data Security</h2>
          </div>
          
          <p>We implement industry-standard security measures to protect your information:</p>

          <div className="subsection">
            <h3>Technical Safeguards</h3>
            <ul>
              <li><strong>Encryption in transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS/SSL</li>
              <li><strong>Encryption at rest:</strong> Sensitive data is encrypted when stored in our databases</li>
              <li><strong>Secure payment processing:</strong> We use PCI-DSS compliant payment processors (Stripe)</li>
              <li><strong>Password protection:</strong> Passwords are hashed using bcrypt or similar algorithms</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Organizational Safeguards</h3>
            <ul>
              <li>Access to personal data is limited to authorized personnel only</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Employee training on data protection and privacy</li>
              <li>Incident response procedures for data breaches</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Your Responsibilities</h3>
            <ul>
              <li>Keep your password secure and confidential</li>
              <li>Log out after using shared computers</li>
              <li>Notify us immediately if you suspect unauthorized access</li>
              <li>Enable two-factor authentication if available</li>
            </ul>
          </div>

          <div className="warning-box">
            <p>Despite our best efforts, no security system is impenetrable. If we experience a data breach that affects your personal information, we will notify you as required by law.</p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar technologies to enhance your experience:</p>

          <div className="cookie-grid">
            <div className="cookie-card">
              <h4>Essential Cookies</h4>
              <p>Required for the app to function properly:</p>
              <ul>
                <li>Session cookies to keep you logged in</li>
                <li>Authentication tokens</li>
                <li>Security cookies to prevent fraud</li>
              </ul>
            </div>

            <div className="cookie-card">
              <h4>Analytics Cookies</h4>
              <p>Help us understand how you use the app:</p>
              <ul>
                <li>Google Analytics to track page views and user flows</li>
                <li>Heatmaps to see where users click</li>
                <li>Performance monitoring tools</li>
              </ul>
            </div>

            <div className="cookie-card">
              <h4>Preference Cookies</h4>
              <p>Remember your settings and choices:</p>
              <ul>
                <li>Language preferences</li>
                <li>Display settings</li>
                <li>Previously selected options</li>
              </ul>
            </div>
          </div>

          <p><strong>Managing Cookies:</strong> You can control cookies through your browser settings. Note that disabling essential cookies may affect app functionality.</p>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Globe size={24} />
            <h2>International Data Transfers</h2>
          </div>
          
          <p>Research Methodology Wizard is operated from the United Kingdom. If you access the app from outside this jurisdiction, your information may be transferred to, stored, and processed in the UK or other countries where our service providers operate.</p>
          
          <p>We ensure that international data transfers comply with applicable data protection laws, including:</p>
          <ul>
            <li>EU-US Data Privacy Framework (if applicable)</li>
            <li>Standard Contractual Clauses (SCCs)</li>
            <li>Adequate safeguards as required by GDPR or other regulations</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Users size={24} />
            <h2>Children's Privacy</h2>
          </div>
          
          <p>Research Methodology Wizard is not intended for users under the age of 16. We do not knowingly collect personal information from children under 16. If you believe we have inadvertently collected information from a child, please contact us immediately, and we will take steps to delete such information.</p>
        </section>

        <section className="legal-section">
          <h2>Third-Party Links and Services</h2>
          <p>Our app may contain links to external websites or integrate with third-party services (e.g., citation databases, statistical software). We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing them with any information.</p>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Bell size={24} />
            <h2>Changes to This Privacy Policy</h2>
          </div>
          
          <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:</p>
          <ul>
            <li>Update the "Last Updated" date at the top of this policy</li>
            <li>Notify you via email (to the address associated with your account)</li>
            <li>Display a prominent notice on the app</li>
            <li>Provide you with 30 days' notice before changes take effect</li>
          </ul>
          <p>Your continued use of Research Methodology Wizard after the effective date of the updated Privacy Policy constitutes your acceptance of the changes.</p>
        </section>

        <section className="legal-section contact-section">
          <div className="section-header-icon">
            <Mail size={24} />
            <h2>Contact Us</h2>
          </div>
          
          <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
          
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a></p>
            <p><strong>Support Email:</strong> <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
